import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ShieldX } from 'lucide-react';
import { Button } from '@/components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AuthCallback = () => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);
  const [authError, setAuthError] = useState(null);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processSession = async () => {
      const hash = window.location.hash;
      const params = new URLSearchParams(hash.substring(1));
      const sessionId = params.get('session_id');

      if (!sessionId) {
        navigate('/login');
        return;
      }

      try {
        const response = await axios.post(
          `${BACKEND_URL}/api/auth/session`,
          { session_id: sessionId },
          { withCredentials: true }
        );

        navigate('/admin/dashboard', { state: { user: response.data }, replace: true });
      } catch (error) {
        console.error('Auth error:', error);
        const status = error.response?.status;
        const detail = error.response?.data?.detail;

        if (status === 403) {
          setAuthError(detail || 'You are not authorized to access this system. Please contact the administrator for an invitation.');
        } else {
          navigate('/login', { state: { error: 'Authentication failed. Please try again.' }, replace: true });
        }
      }
    };

    processSession();
  }, [navigate]);

  if (authError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50 p-4">
        <div data-testid="auth-error-card" className="max-w-md w-full bg-white border border-red-200 rounded-lg shadow-sm p-8 text-center space-y-5">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-red-50">
            <ShieldX className="w-7 h-7 text-red-600" />
          </div>
          <h2 className="text-xl font-semibold text-stone-900">Access Denied</h2>
          <p data-testid="auth-error-message" className="text-sm text-stone-600 leading-relaxed">{authError}</p>
          <Button
            data-testid="back-to-login-btn"
            onClick={() => navigate('/login', { replace: true })}
            className="bg-amber-700 hover:bg-amber-800 text-white"
          >
            Back to Login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-stone-50">
      <div className="text-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-amber-700"></div>
        <p className="mt-4 text-stone-600">Authenticating...</p>
      </div>
    </div>
  );
};

export default AuthCallback;